import { useState } from 'react';
import type { ScreenContent } from '@lma/shared';
import { ContentScreen } from './screens-obs';

/**
 * Экраны трёх продуктов: паспорт лица, программа «Жизнь в меняющемся теле»,
 * онлайн-консультация. Тексты приходят из содержания, код знает только, какие
 * кнопки показать при каком доступе.
 */

/** Предзапись открывается 26 октября по Москве. Дата из ТЗ 12 недель. */
export const PREENROLL_OPENS_AT = new Date('2026-10-26T00:00:00+03:00');

export type ProductKind = 'passport14' | 'program12' | 'consult222';

/** Доступы женщины по продуктам программы и паспорта. */
export interface ProductOwnership {
  passport14: boolean;
  stage1: boolean;
  full: boolean;
  preenrolled: boolean;
}

type ActionKind = 'primary' | 'secondary' | 'tertiary';
interface CardAction {
  key: string;
  kind: ActionKind;
  label: string;
  onClick: () => void;
}

/** Паспорт лица: купить или открыть канал, если доступ уже есть. */
export function PassportCard({
  screen,
  owned,
  onBuy,
  onOpenChannel,
  onBack,
}: {
  screen: ScreenContent;
  owned: ProductOwnership;
  onBuy: () => void;
  onOpenChannel: () => void;
  onBack: () => void;
}) {
  const actions: CardAction[] = owned.passport14
    ? [
        {
          key: 'channel',
          kind: 'primary',
          label: 'Открыть канал «14 дней»',
          onClick: onOpenChannel,
        },
      ]
    : [{ key: 'buy', kind: 'primary', label: 'Начать 14 дней · 77 €', onClick: onBuy }];
  actions.push({ key: 'back', kind: 'tertiary', label: 'Назад', onClick: onBack });

  return <ContentScreen screen={screen} actions={actions} />;
}

/**
 * Программа: два способа оплаты, доплата тем, кто уже на первом этапе,
 * предзапись до старта и канал после оплаты.
 */
export function ProgramCard({
  screen,
  owned,
  onPayFull,
  onPayStage1,
  onTopUp,
  onPreenroll,
  onOpenChannel,
  onBack,
}: {
  screen: ScreenContent;
  owned: ProductOwnership;
  onPayFull: () => void;
  onPayStage1: () => void;
  onTopUp: () => void;
  onPreenroll: () => void;
  onOpenChannel: () => void;
  onBack: () => void;
}) {
  const now = new Date();
  const hasProgram = owned.full || owned.stage1;
  const actions: CardAction[] = [];
  let note: string | null = null;

  if (owned.full) {
    actions.push({
      key: 'channel',
      kind: 'primary',
      label: 'Открыть канал программы',
      onClick: onOpenChannel,
    });
  } else if (owned.stage1) {
    actions.push({
      key: 'channel',
      kind: 'primary',
      label: 'Открыть канал программы',
      onClick: onOpenChannel,
    });
    actions.push({
      key: 'topup',
      kind: 'secondary',
      label: 'Доплатить 178 € до конца программы',
      onClick: onTopUp,
    });
  } else if (now < PREENROLL_OPENS_AT) {
    note = 'Предзапись откроется 26 октября. Напомним, когда начнётся набор.';
  } else if (owned.preenrolled) {
    note = 'Вы в предзаписи. Напомним 9 ноября, когда программа начнётся.';
  } else {
    actions.push({
      key: 'pay_full',
      kind: 'primary',
      label: 'Оплатить сразу 333 €',
      onClick: onPayFull,
    });
    actions.push({
      key: 'pay_stage1',
      kind: 'secondary',
      label: 'Начать с первых 4 недель · 155 €',
      onClick: onPayStage1,
    });
    actions.push({
      key: 'preenroll',
      kind: 'tertiary',
      label: 'Записаться на предзапись',
      onClick: onPreenroll,
    });
  }

  if (!hasProgram && now < PREENROLL_OPENS_AT) {
    // До 26 октября покупка не открыта: показываем даты, без кнопок оплаты.
    actions.length = 0;
  }

  actions.push({ key: 'back', kind: 'tertiary', label: 'Назад', onClick: onBack });

  return (
    <ContentScreen screen={screen} actions={actions}>
      {note ? <p className="product-note">{note}</p> : null}
    </ContentScreen>
  );
}

/** Онлайн-консультация: текст и цена из содержания, запись — через WhatsApp с готовым сообщением. */
export function ConsultCard({
  screen,
  onBook,
  onBack,
}: {
  screen: ScreenContent;
  onBook: () => void;
  onBack: () => void;
}) {
  return (
    <ContentScreen
      screen={screen}
      actions={[
        {
          key: 'book',
          kind: 'primary',
          label: 'Записаться на консультацию — 222 €',
          onClick: onBook,
        },
        { key: 'back', kind: 'tertiary', label: 'Назад', onClick: onBack },
      ]}
    />
  );
}
